import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRequest, patchRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  return `${backendBase}/${normalizedPath}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const UpdateDepartment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState({
    heading: '',
    subheading: '',
    image: null,
    highlights: '',
    vision: '',
    mission: '',
    status: 'active',
  });

  useEffect(() => {
    const fetchDepartment = async () => {
      setFetching(true);
      try {
        const res = await getRequest(`departments/${id}`);
        if (res?.data?.success) {
          const item = res.data.data;
          setCurrent(item);
          setFormData({
            heading: item.heading || '',
            subheading: item.subheading || '',
            image: null,
            highlights: item.highlights || '',
            vision: item.vision || '',
            mission: item.mission || '',
            status: item.status || 'active',
          });
        } else {
          toast.error(res?.data?.message || 'Failed to load department');
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Error fetching department');
      } finally {
        setFetching(false);
      }
    };

    if (id) fetchDepartment();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (e.target.type === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files && files.length > 0 ? files[0] : null,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.heading.trim()) {
      toast.error('Heading is required');
      return;
    }

    setLoading(true);

    const payload = new FormData();
    payload.append('heading', formData.heading.trim());
    payload.append('subheading', formData.subheading.trim());
    payload.append('highlights', formData.highlights.trim());
    payload.append('vision', formData.vision.trim());
    payload.append('mission', formData.mission.trim());
    payload.append('status', formData.status);

    if (formData.image) {
      payload.append('image', formData.image);
    }

    try {
      const response = await patchRequest(`departments/${id}`, payload, getAuthHeaders());

      if (response?.data?.success) {
        toast.success('Department updated successfully');
        navigate('/departments');
      } else {
        toast.error(response?.data?.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating department');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="text-center mt-5">Loading department...</div>;

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="font-weight-bold">Update Department</h4>
      </div>

      <form onSubmit={handleSubmit} className="form-style">
        <div className="row mx-4 mt-5">
          <div className="col-md-6 mt-2">
            <label htmlFor="heading">Heading *</label>
            <input
              id="heading"
              name="heading"
              className="form-control"
              value={formData.heading}
              onChange={handleChange}
              required
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="subheading">Subheading (optional)</label>
            <input
              id="subheading"
              name="subheading"
              className="form-control"
              value={formData.subheading}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="image">Image (Leave blank to keep current)</label>
            <input
              id="image"
              name="image"
              type="file"
              className="form-control-file"
              accept="image/*"
              onChange={handleChange}
            />
            {formData.image && (
              <small className="text-muted d-block mt-1">Selected: {formData.image.name}</small>
            )}
            {!formData.image && current?.image && (
              <div className="mt-2">
                <img
                  src={getImageUrl(current.image)}
                  alt={current.heading}
                  width={120}
                  style={{ borderRadius: '4px', objectFit: 'cover' }}
                />
              </div>
            )}
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              className="form-control"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="col-12 mt-2">
            <label htmlFor="highlights">Highlights (optional)</label>
            <textarea
              id="highlights"
              name="highlights"
              rows="3"
              className="form-control"
              value={formData.highlights}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="vision">Vision (optional)</label>
            <textarea
              id="vision"
              name="vision"
              rows="4"
              className="form-control"
              value={formData.vision}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="mission">Mission (optional)</label>
            <textarea
              id="mission"
              name="mission"
              rows="4"
              className="form-control"
              value={formData.mission}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="mt-5 text-right mx-4">
          <button type="submit" className="btn btn-lg addbtn" disabled={loading}>
            {loading ? 'Updating...' : 'Update Department'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateDepartment;